import type { ProductTier } from './config'

/** SuperAdmin instance/committee scope configuration -- see vault "Customer Locality
 *  Scoping — State & District Data Access" and Task 12's backend contract. */

export type GrantedBy = 'Self' | 'Sponsored'

export interface InstanceConfigDto {
  productTier: ProductTier
  customerName: string | null
}

export interface SetInstanceConfigRequest {
  tier: ProductTier
  customerName?: string
}

export interface ScopeDistrictEntry {
  districtType: string
  districtCode: string
}

/**
 * WL-4a: the fixed district-type tokens stored in `LocalityGrantDistrict.DistrictType`. The PascalCase
 * token — not the display label — is the FORWARD CONTRACT for deferred district-level row enforcement,
 * which will match these against the `VoterDistricts.CongressionalDistrict` / `StateHouseDistrict` /
 * `StateSenateDistrict` columns. Standardized deliberately here so the editor can't seed inconsistent
 * strings (the app previously carried `StateHouse`/`StateSenate`/`USHouse` and lowercase `congressional`).
 */
export const DISTRICT_TYPE_OPTIONS = [
  { token: 'StateHouse', label: 'State House' },
  { token: 'StateSenate', label: 'State Senate' },
  { token: 'Congressional', label: 'Congressional' },
] as const

export type DistrictTypeToken = (typeof DISTRICT_TYPE_OPTIONS)[number]['token']

/**
 * WL-4b (Task 8): the DISTINCT verbatim district codes present in the held voter file for one
 * state, split by chamber -- backs `GET /api/admin/districts/catalog?state=` (Task 7). Feeds the
 * grant editor's district-code dropdown so a SuperAdmin picks a real code instead of free-texting
 * one that doesn't exist in the voter pool.
 */
export interface DistrictCatalogResponse {
  state: string
  congressional: string[]
  stateSenate: string[]
  stateHouse: string[]
}

/** Maps a `DISTRICT_TYPE_OPTIONS` token to its field on `DistrictCatalogResponse`. */
export const CATALOG_FIELD_BY_DISTRICT_TYPE: Record<DistrictTypeToken, keyof Omit<DistrictCatalogResponse, 'state'>> = {
  Congressional: 'congressional',
  StateSenate: 'stateSenate',
  StateHouse: 'stateHouse',
}

export interface ScopeStateEntry {
  state: string
  districts: ScopeDistrictEntry[]
}

export interface CommitteeScopeConfigDto {
  committeeId: string
  isUnrestricted: boolean
  grantedBy: GrantedBy
  sponsorCommitteeId?: string | null
  homeUnionId?: string | null
  defaultMapState?: string | null
  defaultMapDistrictType?: string | null
  defaultMapDistrictCode?: string | null
  states: ScopeStateEntry[]
}

export interface ScopeGrantWriteRequest {
  isUnrestricted: boolean
  grantedBy: GrantedBy
  sponsorCommitteeId?: string
  states: ScopeStateEntry[]
}

export interface ScopeDefaultsWriteRequest {
  homeUnionId?: string
  defaultMapState?: string
  defaultMapDistrictType?: string
  defaultMapDistrictCode?: string
}
